import React, { useEffect, useState, useCallback } from 'react';
import { authFetch } from '../shopify/authFetch';
import { fetchUsageAndPlan, withNonce } from '../services/livePlan';
import AuthStatus from './AuthStatus';
import { redirectToShopifyBilling } from '../utils/billingRedirect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ENABLE_MANAGED = (process.env.REACT_APP_ENABLE_MANAGED_PRICING || 'false') === 'true';

export default function ManagedBilling() {
  const [status, setStatus] = useState(null);
  const [planData, setPlanData] = useState(null); // Enhanced plan data from usage endpoint
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState('');
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const [refreshing, setRefreshing] = useState(false); // Track refreshing state
  const [needsReauth, setNeedsReauth] = useState(false); // Track if re-auth is needed

  const getShop = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get('shop') || '';
  };

  const getStoreHandle = () => {
    const shop = getShop();
    return shop ? shop.replace('.myshopify.com', '') : '';
  };

  // Detect billing return from URL parameters (enhanced detection)
  const detectBillingReturn = () => {
    const params = new URLSearchParams(window.location.search);
    return (
      params.get('billing') === 'success' || 
      params.get('shopify_billing') === '1' ||
      params.get('charge_id') !== null ||  // Shopify often adds charge_id after billing
      params.get('subscription_id') !== null || // Alternative Shopify billing parameter
      params.has('billing_success') || // Custom parameter we might set
      (params.get('embedded') === '1' && window.document.referrer.includes('admin.shopify.com')) // Return from admin
    );
  };

  // Clean billing return parameters from URL (enhanced cleanup)
  const cleanBillingReturnParams = () => {
    const url = new URL(window.location);
    const params = url.searchParams;
    
    // Remove all possible billing return parameters
    [
      'billing', 'nonce', 'ts', 'shopify_billing', 
      'charge_id', 'subscription_id', 'billing_success',
      'billing_status', 'payment_method', 'confirmation_url'
    ].forEach(param => {
      params.delete(param);
    });
    
    // Update URL without causing page reload
    const cleanUrl = url.pathname + (params.toString() ? '?' + params.toString() : '');
    window.history.replaceState(null, '', cleanUrl);
  };

  // Enhanced plan data fetching function
  const fetchPlanData = useCallback(async (forceRefresh = false, billingReturn = false) => {
    const shop = getShop();
    if (!shop) return;

    try {
      setError('');
      
      // Use the enhanced usage endpoint that provides live Shopify plan data
      // Force refresh for billing returns or explicit requests
      const data = await fetchUsageAndPlan(shop, { 
        forcePlanRefresh: forceRefresh, 
        billingReturn 
      });
      
      setPlanData(data);
      setLastRefreshTime(new Date());
      
      
      return data;
    } catch (e) {
      console.error('❌ Failed to fetch enhanced plan data:', e.message);
      setError(`Failed to load plan data: ${e.message}`);
      return null;
    }
  }, []);

  // Force manual subscription refresh via backend API (for immediate billing sync)
  const forceManualRefresh = useCallback(async (shop) => {
    
    try {
      const response = await authFetch(`${BACKEND_URL}/api/webhooks/admin/subscription/refresh?shop=${encodeURIComponent(shop)}`, {
        method: 'POST',
        headers: {
          'x-api-key': 'HX4rA9!dj7%pQa12zLbT3V', // Use backend API key for internal calls
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.detail || 'Manual refresh failed');
      }
      
      return result;
    } catch (e) {
      console.error('❌ Manual subscription refresh failed:', e);
      return null;
    }
  }, []);

  // Force refresh plan data (called after billing changes)
  const refreshPlanData = useCallback(async (billingReturn = false) => {
    setRefreshing(true);
    setLoading(true);
    
    try {
      // For billing returns, force immediate sync via manual refresh endpoint
      if (billingReturn) {
        const shop = getShop();
        if (shop) {
          await forceManualRefresh(shop);
          
          // Wait a moment for database update to propagate
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
      
      await fetchPlanData(true, billingReturn); // Force cache bust with billing context
    } catch (e) {
      console.error('❌ Failed to refresh plan data:', e);
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, [fetchPlanData, forceManualRefresh]);

  const redirectToHostedPricing = () => {
    setError('');
    if (!redirectToShopifyBilling(getShop())) {
      setRedirecting(false);
      setError('Billing could not be opened. Please check the Shopify app billing configuration or contact support.');
      return;
    }
    setRedirecting(true);
  };

  useEffect(() => {
    let mounted = true;
    
    const fetchStatus = async () => {
      setError('');
      try {
        const res = await authFetch(`${BACKEND_URL}/api/billing/status`, { method: 'GET' });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.detail || 'Failed to load billing status');
        if (!mounted) return;
        setStatus(data);
        
        // Only auto-redirect if the merchant has already gone through plan selection.
        // If plan_selected is false, PlanSelectionOnboarding handles the billing flow.
        let planAlreadySelected = true;
        const shopForCheck = getShop();
        if (shopForCheck) {
          try {
            const statusRes = await fetch(`${BACKEND_URL}/api/setup/status?shop=${encodeURIComponent(shopForCheck)}`);
            if (statusRes.ok) {
              const statusData = await statusRes.json();
              planAlreadySelected = statusData.planSelected !== false;
            }
          } catch (_) { /* non-fatal — default to allowing redirect */ }
        }
        // Trial-first onboarding: merchants choose when to open billing.
        // Never automatically redirect an unpaid merchant away from the app.
      } catch (e) {
        if (!mounted) return;
        setError(e.message || 'Failed to load billing status');
      }
    };

    // Check for billing return and handle immediately 
    const handleBillingReturn = async () => {
      if (detectBillingReturn()) {
        
        // Clean URL parameters to prevent loop
        cleanBillingReturnParams();
        
        // Force immediate revalidation with billing context
        await refreshPlanData(true);
        
        // Show success feedback
      }
    };

    // Initialize data and handle billing returns
    const initializeData = async () => {
      // Handle billing return first if present
      await handleBillingReturn();
      
      // Then fetch normal data (skip refresh if we just handled billing return)
      const isBillingReturn = detectBillingReturn();
      if (!isBillingReturn) {
        await fetchPlanData(false); // Don't force refresh on normal load
      }
      
      await fetchStatus();
    };

    initializeData();
    
    return () => { mounted = false; };
  }, [fetchPlanData, refreshPlanData]);

  // Add visibility change and focus listeners for plan revalidation
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refreshPlanData(false);
      }
    };

    const handleWindowFocus = () => {
      refreshPlanData(false);
    };

    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [refreshPlanData]);

  // Handle auth status changes
  const handleAuthStatusChange = useCallback((authStatus) => {
    setNeedsReauth(authStatus?.needs_reauth || false);
    
    // If auth becomes valid, refresh plan data
    if (authStatus?.token_valid && !authStatus?.needs_reauth) {
      refreshPlanData(false);
    }
  }, [refreshPlanData]);

  return (
    <div className="billing-card">
      {/* Show auth status component at the top */}
      <AuthStatus 
        shop={getShop()} 
        onAuthStatusChange={handleAuthStatusChange}
      />
      
      <div className="billing-card-header">
        <h3>Billing Management</h3>
        <p>Plans are managed on Shopify. Use the button below to view and change your plan.</p>
        
        {!getShop() && (
          <div className="billing-notice">
            <div className="notice-icon">💡</div>
            <div className="notice-content">
              <strong>Best Experience:</strong> Access this app from within your Shopify Admin → Apps section for seamless billing management.
            </div>
          </div>
        )}

        {lastRefreshTime && (
          <div className="billing-notice" style={{ fontSize: '0.8em', color: '#666' }}>
            <div className="notice-icon">🔄</div>
            <div className="notice-content">
              Plan data last updated: {lastRefreshTime.toLocaleTimeString()}
            </div>
          </div>
        )}
      </div>
      
      <div className="billing-card-body">
        <button 
          className="btn btn-primary btn-large" 
          onClick={redirectToHostedPricing}
          disabled={redirecting}
        >
          {redirecting ? (
            <>
              <div className="button-spinner"></div>
              Redirecting to Shopify...
            </>
          ) : (
            '🏪 Manage plan in Shopify'
          )}
        </button>
        
        {/* Refresh Plan Data button removed - Shopify managed billing works perfectly */}
        
        {error && (
          <div className="billing-error">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}
        
        {/* Enhanced plan data display */}
        {planData && (
          <div className="billing-status">
            <h4>Current Plan Details (Live from Shopify)</h4>
            <div className="status-grid">
              <div className="status-item">
                <span className="status-label">Plan:</span>
                <span className="status-value plan-name">
                  {planData.plan?.name || 'Unknown'}
                </span>
              </div>
              <div className="status-item">
                <span className="status-label">Handle:</span>
                <span className="status-value">
                  {planData.plan?.handle || 'unknown'}
                </span>
              </div>
              <div className="status-item">
                <span className="status-label">Usage:</span>
                <span className="status-value">
                  {planData.usage?.used || 0} / {planData.plan?.limit || 0}
                </span>
              </div>
              <div className="status-item">
                <span className="status-label">Remaining:</span>
                <span className="status-value">
                  {planData.usage?.remaining || 0}
                </span>
              </div>
              {planData.plan?.pricing?.amount && (
                <div className="status-item">
                  <span className="status-label">Price:</span>
                  <span className="status-value">
                    {planData.plan.pricing.amount} {planData.plan.pricing.currency || 'USD'}
                  </span>
                </div>
              )}
              <div className="status-item">
                <span className="status-label">Status:</span>
                <span className={`status-badge status-${planData.plan?.subscription?.status || 'unknown'}`}>
                  {planData.plan?.subscription?.status || 'Unknown'}
                </span>
              </div>
              <div className="status-item">
                <span className="status-label">Test Mode:</span>
                <span className={`test-mode-badge ${planData.plan?.subscription?.test ? 'test-active' : 'test-inactive'}`}>
                  {String(planData.plan?.subscription?.test || false)}
                </span>
              </div>
              <div className="status-item">
                <span className="status-label">Data Source:</span>
                <span className="status-value">
                  {planData.data_source || 'unknown'}
                </span>
              </div>
              <div className="status-item">
                <span className="status-label">Shop:</span>
                <span className="status-value shop-domain">
                  {planData.shop}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Legacy status display for fallback */}
        {status && !planData && (
          <div className="billing-status">
            <h4>Current Subscription Details (Legacy)</h4>
            <div className="status-grid">
              <div className="status-item">
                <span className="status-label">Active:</span>
                <span className={`status-value ${status.active ? 'status-active' : 'status-inactive'}`}>
                  {String(status.active)}
                </span>
              </div>
              <div className="status-item">
                <span className="status-label">Status:</span>
                <span className={`status-badge status-${status.status || 'unknown'}`}>
                  {status.status || 'Unknown'}
                </span>
              </div>
              <div className="status-item">
                <span className="status-label">Plan:</span>
                <span className="status-value plan-name">
                  {status.plan || 'None'}
                </span>
              </div>
              <div className="status-item">
                <span className="status-label">Billing Period:</span>
                <span className="status-value">
                  {status.interval || '—'}
                </span>
              </div>
              {(status.amount != null && status.currency) && (
                <div className="status-item">
                  <span className="status-label">Price:</span>
                  <span className="status-value">
                    {status.amount} {status.currency}
                  </span>
                </div>
              )}
              <div className="status-item">
                <span className="status-label">Next Renewal:</span>
                <span className="status-value">
                  {status.current_period_end || '—'}
                </span>
              </div>
              <div className="status-item">
                <span className="status-label">Test Mode:</span>
                <span className={`test-mode-badge ${status.test_mode ? 'test-active' : 'test-inactive'}`}>
                  {String(status.test_mode)}
                </span>
              </div>
              <div className="status-item">
                <span className="status-label">Shop Domain:</span>
                <span className="status-value shop-domain">
                  {status.shop_domain}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
